import chevronRight from "@assets/images/chevron-right.svg";
import { TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import TableCellHeader from "@saleor/components/TableCellHeader";
import {
  CartDetailCustomerSortField,
  createOrderUrl,
  orderDetailUrl
} from "@saleor/createOrder/urls";
import EmptyCartTable from "@saleor/createOrderExport/components/EmptyCartTable";
import { useCustomerBlockContext } from "@saleor/customerBlock/hook/useCustomerBlockContext";
import {
  CustomerCartsQuery,
  useGetCreditLimitLazyQuery
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { RelayToFlat, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

interface ICartTableCustomerProps
  extends SortPage<CartDetailCustomerSortField> {
  data: RelayToFlat<CustomerCartsQuery["customerCarts"]>;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      border: "1px solid #E0E0E0"
    },
    centeredHeader: {
      "& > div": {
        justifyContent: "center"
      }
    },
    centeredCell: {
      textAlign: "center",
      color: theme.palette.grey[800],
      fontWeight: "bold",
      "& img": {
        cursor: "pointer"
      }
    },
    rowPointer: {
      cursor: "pointer"
    },
    tableHead: {
      "& .MuiTableCell-root, .MuiTableCell-root > div": {
        color: theme.palette.grey[600],
        fontWeight: "bold"
      }
    },
    colId: {
      color: theme.palette.grey[900]
    },
    colName: {
      color: theme.palette.grey[800]
    },
    colDetail: {
      color: theme.palette.grey[900]
    },
    noBold: {
      "& > div": {
        fontWeight: [400, "!important"] as any
      }
    }
  }),
  { name: "CartTable" }
);

const CartTableCustomer: React.FC<ICartTableCustomerProps> = ({
  data,
  sort,
  onSort
}) => {
  const classes = useStyles();
  const navigate = useNavigator();
  const [selectedRowId, setSelectedRowId] = useState<string>("");
  const { company } = useUser();
  const { onOpenCreditBlockDialog } = useCustomerBlockContext();

  const [getCreditLimitLazy] = useGetCreditLimitLazyQuery({
    onCompleted: data => {
      if (data?.getCreditLimit?.creditBlockStatus) {
        onOpenCreditBlockDialog();
      } else {
        navigate(orderDetailUrl(selectedRowId));
      }
    }
  });

  const onTableRowClick = data => {
    getCreditLimitLazy({
      variables: {
        input: {
          soldToCode: company,
          contractNo: data?.contract?.code
        }
      }
    });

    setSelectedRowId(data?.id);
  };

  return (
    <div className={classes.root}>
      {data?.length > 0 ? (
        <ResponsiveTable>
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCellHeader>
                <FormattedMessage defaultMessage="เลขที่ Contract" />
              </TableCellHeader>
              <TableCellHeader>
                <FormattedMessage defaultMessage="ชื่อโครงการ" />
              </TableCellHeader>
              <TableCellHeader
                className={classes.centeredHeader}
                direction={
                  sort.sort === CartDetailCustomerSortField.linesCount
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(CartDetailCustomerSortField.linesCount)}
              >
                <FormattedMessage defaultMessage="สินค้าในตะกร้า" />
              </TableCellHeader>
              <TableCellHeader
                className={classNames(classes.centeredHeader, classes.noBold)}
              >
                <FormattedMessage defaultMessage="ดูสินค้าในตะกร้า" />
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data &&
              renderCollection(data, item => (
                <TableRow
                  key={item.id}
                  onClick={() => onTableRowClick(item)}
                  className={classes.rowPointer}
                >
                  <TableCell className={classes.colDetail}>
                    {item.contract?.code}
                  </TableCell>
                  <TableCell className={classes.colName}>
                    {item.contract?.projectName}
                  </TableCell>
                  <TableCell className={classes.centeredCell}>
                    {item.quantity}
                  </TableCell>
                  <TableCell className={classes.centeredCell}>
                    <img src={chevronRight} alt="" />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </ResponsiveTable>
      ) : (
        <EmptyCartTable
          message="ไปหน้า My Contract เพื่อช้อปปิ้ง"
          onBack={() => navigate(createOrderUrl)}
        />
      )}
    </div>
  );
};

export default CartTableCustomer;
